import { useEffect, useRef } from "react";
import {
  mxClient,
  mxUtils,
  mxGraph,
  mxCodec,
  mxPoint,
  mxGeometry,
  mxGraphModel,
  mxCellRenderer,
  mxStyleRegistry,
  mxEdgeStyle,
  mxConstants,
  mxGraphView
} from "../../utils/graph/mxModule";
import { registerCustomShapes, registerCustomShapesForStick } from "../../utils/graph/registerShapes";
import { PREDEFINED_LIBRARIES } from "../../constants/Constants";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

const GraphPreview = ({ title, diagram_xml, onClose }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!mxClient.isBrowserSupported()) {
      console.error("mxGraph is not supported.");
      return;
    }

    const container = containerRef.current;
    container.innerHTML = "";

    const graph = new mxGraph(container);

    // Allow HTML in labels
    graph.setHtmlLabels(true);

    // Computes the position of edge to edge connection points.
    mxGraphView.prototype.updateFixedTerminalPoint = function (
      edge,
      terminal,
      source,
      constraint
    ) {
      var pt = null;
      if (constraint != null) {
        pt = this.graph.getConnectionPoint(terminal, constraint);
      }

      if (source) {
        edge.sourceSegment = null;
      } else {
        edge.targetSegment = null;
      }

      if (pt == null) {
        var s = this.scale;
        var tr = this.translate;
        var orig = edge.origin;
        var geo = this.graph.getCellGeometry(edge.cell);
        pt = geo.getTerminalPoint(source);

        // Computes edge-to-edge connection point
        if (pt != null) {
          pt = new mxPoint(
            s * (tr.x + pt.x + orig.x),
            s * (tr.y + pt.y + orig.y)
          );

          // Finds nearest segment on edge and computes intersection
          if (terminal != null && terminal.absolutePoints != null) {
            var seg = mxUtils.findNearestSegment(terminal, pt.x, pt.y);

            // Finds orientation of the segment
            var p0 = terminal.absolutePoints[seg];
            var pe = terminal.absolutePoints[seg + 1];
            var horizontal = p0.x - pe.x == 0;

            // Stores the segment in the edge state
            var key = source ? "sourceConstraint" : "targetConstraint";
            var value = horizontal ? "horizontal" : "vertical";
            edge.style[key] = value;

            // Keeps the coordinate within the segment bounds
            if (horizontal) {
              pt.x = p0.x;
              pt.y = Math.min(pt.y, Math.max(p0.y, pe.y));
              pt.y = Math.max(pt.y, Math.min(p0.y, pe.y));
            } else {
              pt.y = p0.y;
              pt.x = Math.min(pt.x, Math.max(p0.x, pe.x));
              pt.x = Math.max(pt.x, Math.min(p0.x, pe.x));
            }
          }
        }
        // Computes constraint connection points on vertices and ports
        else if (terminal != null && terminal.cell.geometry.relative) {
          pt = new mxPoint(
            this.getRoutingCenterX(terminal),
            this.getRoutingCenterY(terminal)
          );
        }

        // Snaps point to grid
        /*if (pt != null)
      {
        var tr = this.graph.view.translate;
        var s = this.graph.view.scale;
        
        pt.x = (this.graph.snap(pt.x / s - tr.x) + tr.x) * s;
        pt.y = (this.graph.snap(pt.y / s - tr.y) + tr.y) * s;
      }*/
      }

      edge.setAbsoluteTerminalPoint(pt, source);
    };


    mxEdgeStyle.WireConnector = function (
      state,
      source,
      target,
      hints,
      result
    ) {
      // Creates array of all way- and terminalpoints
      var pts = state.absolutePoints;
      var horizontal = true;
      var hint = null;

      // Gets the initial connection from the source terminal or edge
      if (source != null && state.view.graph.model.isEdge(source.cell)) {
        horizontal = state.style["sourceConstraint"] === "horizontal";
      } else if (source != null) {
        horizontal = source.style["portConstraint"] !== "vertical";

        // Checks the direction of the shape and rotates
        var direction = source.style[mxConstants.STYLE_DIRECTION];

        if (direction === "north" || direction === "south") {
          horizontal = !horizontal;
        }
      }

      // Adds the first point
      // TODO: Should move along connected segment
      var pt = pts[0];

      if (pt == null && source != null) {
        pt = new mxPoint(
          state.view.getRoutingCenterX(source),
          state.view.getRoutingCenterY(source)
        );
      } else if (pt != null) {
        pt = pt.clone();
      }

      var first = pt;

      // Adds the waypoints
      if (hints != null && hints.length > 0) {
        // FIXME: First segment not movable
        /*hint = state.view.transformControlPoint(state, hints[0]);
      mxLog.show();
      mxLog.debug(hints.length,'hints0.y='+hint.y, pt.y)
        
      if (horizontal && Math.floor(hint.y) != Math.floor(pt.y))
      {
        mxLog.show();
        mxLog.debug('add waypoint');

        pt = new mxPoint(pt.x, hint.y);
        result.push(pt);
        pt = pt.clone();
        //horizontal = !horizontal;
      }*/

        for (var i = 0; i < hints.length; i++) {
          horizontal = !horizontal;
          hint = state.view.transformControlPoint(state, hints[i]);

          if (horizontal) {
            if (pt.y != hint.y) {
              pt.y = hint.y;
              result.push(pt.clone());
            }
          } else if (pt.x != hint.x) {
            pt.x = hint.x;
            result.push(pt.clone());
          }
        }
      } else {
        hint = pt;
      }

      // Adds the last point
      pt = pts[pts.length - 1];

      // TODO: Should move along connected segment
      if (pt == null && target != null) {
        pt = new mxPoint(
          state.view.getRoutingCenterX(target),
          state.view.getRoutingCenterY(target)
        );
      }

      if (horizontal) {
        if (pt.y != hint.y && first.x != pt.x) {
          result.push(new mxPoint(pt.x, hint.y));
        }
      } else if (pt.x != hint.x && first.y != pt.y) {
        result.push(new mxPoint(hint.x, pt.y));
      }
    };

    mxStyleRegistry.putValue("wireEdgeStyle", mxEdgeStyle.WireConnector);
    graph.cellsEditable = false;
    var style = graph.getStylesheet().getDefaultEdgeStyle();
    delete style["endArrow"];
    style["edgeStyle"] = "wireEdgeStyle";
    style["orthogonalLoop"] = "1";
    style["strokeColor"] = "#38befe";
    style = graph.getStylesheet().getDefaultVertexStyle();
    style["strokeColor"] = "#00cc66";
    style["fillColor"] ="";

    graph.setEnabled(false);

    const [libraryName, cellName, viewName] = title.props.children.map(libData => libData.props.children.toLowerCase());    
    const isPredefined = PREDEFINED_LIBRARIES.includes(libraryName);

    const parent = graph.getDefaultParent();
    
    if(viewName.toLowerCase().startsWith("stick")){      
      registerCustomShapesForStick();
    }
    else {
      registerCustomShapes();
    }

    let shouldFit = false;
    graph.getModel().beginUpdate();
    try {
    if (isPredefined) {
      const shapeStyle = `shape=${cellName};strokeColor=#00cc66;fillColor=none;circleColor=#517891;`;
      const shapeCtor = mxCellRenderer.defaultShapes[cellName];

      let width = shapeCtor?.config?.width || 40;
      let height = shapeCtor?.config?.height || 60;
      // let width = 40;
      // let height = 60;
        let x = 140;
        if (cellName === 'no-conn') {
          width = 120;
          height = 40;
          x=90;
        }
        graph.insertVertex(parent, null, "", x, 90, width, height, shapeStyle);
      }
    else if (diagram_xml) {
    const xmlDoc = mxUtils.parseXml(diagram_xml);
    window["mxGraphModel"] = mxGraphModel;
    window["mxGeometry"] = mxGeometry;
    window["mxPoint"] = mxPoint;
    const codec = new mxCodec(xmlDoc);
    codec.decode(xmlDoc.documentElement, graph.getModel());
    shouldFit = true;
    // Override schematic-saved light mode font color with white
    Object.values(graph.getModel().cells).forEach(cell => {
      if (cell.vertex) {
        graph.setCellStyles(mxConstants.STYLE_FONTCOLOR, '#ffffff', [cell]);
        let style = graph.getModel().getStyle(cell);
        style = style.replace(/circleColor=[^;]*/, '') + `;circleColor=#517891`;
        graph.getModel().setStyle(cell, style);
      }
      });
    }
    } finally {
      graph.getModel().endUpdate();
    }
    if(shouldFit){
      graph.fit(10); // Adds 10px margin when fitting
    }
  }, [diagram_xml,title]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 9999,
        border: "1px solid lightgray",
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "#fff",
        padding: 1,
        maxWidth:"350px"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",  mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" paddingTop="8px">
          {title}
        </Typography>

        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <div
        ref={containerRef}
        style={{
          width: 320,
          height: 240,
          background: "#000000",
        }}
      />
    </Box>
  );
};

export default GraphPreview;